import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiUpload } from 'react-icons/fi';
import { uploadToServer } from '../../../services/uploadService';

const AuthorsManager = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentAuthor, setCurrentAuthor] = useState({ name: '', bio: '', avatar: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'blog_authors'));
      const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAuthors(list);
    } catch (error) {
      console.error("Erro ao buscar autores:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (currentAuthor.id) {
        await updateDoc(doc(db, 'blog_authors', currentAuthor.id), {
          name: currentAuthor.name,
          bio: currentAuthor.bio,
          avatar: currentAuthor.avatar,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'blog_authors'), {
          name: currentAuthor.name,
          bio: currentAuthor.bio,
          avatar: currentAuthor.avatar,
          createdAt: serverTimestamp()
        });
      }
      setIsEditing(false);
      setCurrentAuthor({ name: '', bio: '', avatar: '' });
      fetchAuthors();
    } catch (error) {
      console.error("Erro ao salvar autor:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este autor?")) {
      try {
        await deleteDoc(doc(db, 'blog_authors', id));
        fetchAuthors();
      } catch (error) {
        console.error("Erro ao deletar:", error);
      }
    }
  };

  const handleEdit = (author) => {
    setCurrentAuthor(author);
    setIsEditing(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const url = await uploadToServer(file, 'blog');
      setCurrentAuthor({ ...currentAuthor, avatar: url });
    } catch (error) {
      console.error('Erro no upload da imagem', error);
      alert('Falha ao fazer upload da imagem.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando autores...</div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gerenciar Autores</h2>
          <p className="text-gray-500">Adicione ou edite autores do blog.</p>
        </div>
        {!isEditing && (
          <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors">
            <FiPlus /> Novo Autor
          </button>
        )}
      </div>

      {isEditing && (
        <form onSubmit={handleSave} className="mb-8 bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do Autor</label>
              <input type="text" required value={currentAuthor.name} onChange={e => setCurrentAuthor({...currentAuthor, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Avatar (Imagem)</label>
              <div className="flex items-center gap-3">
                {currentAuthor.avatar && (
                  <img src={currentAuthor.avatar} alt="Preview" className="w-10 h-10 rounded-full object-cover" />
                )}
                <label className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <FiUpload className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{uploading ? 'Enviando...' : 'Fazer Upload'}</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mini Bio</label>
              <textarea value={currentAuthor.bio} onChange={e => setCurrentAuthor({...currentAuthor, bio: e.target.value})} rows="3" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => { setIsEditing(false); setCurrentAuthor({name:'', bio:'', avatar:''}); }} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
            <button type="submit" disabled={uploading} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"><FiSave /> Salvar</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-sm">
              <th className="pb-3 font-semibold w-16">Foto</th>
              <th className="pb-3 font-semibold">Nome</th>
              <th className="pb-3 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {authors.map(author => (
              <tr key={author.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <td className="py-4">
                  {author.avatar ? (
                    <img src={author.avatar} alt={author.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">{author.name.charAt(0)}</div>
                  )}
                </td>
                <td className="py-4 text-gray-900 dark:text-white font-medium">{author.name}</td>
                <td className="py-4 flex justify-end gap-2">
                  <button onClick={() => handleEdit(author)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg"><FiEdit2 /></button>
                  <button onClick={() => handleDelete(author.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 rounded-lg"><FiTrash2 /></button>
                </td>
              </tr>
            ))}
            {authors.length === 0 && (
              <tr><td colSpan="3" className="py-8 text-center text-gray-500">Nenhum autor encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuthorsManager;
